import { useEffect, useMemo, useState } from "react";
import type { SalesCatalogItemRow } from "@/lib/salesOrgPricingClient";

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function RowEditor(props: {
  row: SalesCatalogItemRow;
  busy: boolean;
  onSave: (id: string, patch: Record<string, unknown>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const { row, busy, onSave, onDelete } = props;
  const [product_line, setProductLine] = useState(row.product_line);
  const [external_product_id, setExt] = useState(row.external_product_id ?? "");
  const [width_ft, setW] = useState(String(row.width_ft));
  const [length_ft, setL] = useState(String(row.length_ft));
  const [package_key, setPkg] = useState(row.package_key ?? "");
  const [options_key, setOpt] = useState(row.options_key ?? "");
  const [msrp, setMsrp] = useState(String(row.msrp));
  const [currency, setCur] = useState(row.currency || "USD");

  useEffect(() => {
    setProductLine(row.product_line);
    setExt(row.external_product_id ?? "");
    setW(String(row.width_ft));
    setL(String(row.length_ft));
    setPkg(row.package_key ?? "");
    setOpt(row.options_key ?? "");
    setMsrp(String(row.msrp));
    setCur(row.currency || "USD");
  }, [
    row.id,
    row.product_line,
    row.external_product_id,
    row.width_ft,
    row.length_ft,
    row.package_key,
    row.options_key,
    row.msrp,
    row.currency,
  ]);

  return (
    <tr className="border-t border-gray-200 dark:border-gray-700 align-top">
      <td className="py-1.5 pr-2 font-mono text-[10px] text-gray-500 whitespace-nowrap" title={row.id}>
        {shortId(row.id)}
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-full min-w-[7rem] text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={external_product_id}
          disabled={busy}
          onChange={(e) => setExt(e.target.value)}
          placeholder="—"
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-full min-w-[10rem] text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={product_line}
          disabled={busy}
          onChange={(e) => setProductLine(e.target.value)}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-12 text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={width_ft}
          disabled={busy}
          onChange={(e) => setW(e.target.value)}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-12 text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={length_ft}
          disabled={busy}
          onChange={(e) => setL(e.target.value)}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-full min-w-[5rem] text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={package_key}
          disabled={busy}
          onChange={(e) => setPkg(e.target.value)}
          placeholder="—"
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-full min-w-[5rem] text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={options_key}
          disabled={busy}
          onChange={(e) => setOpt(e.target.value)}
          placeholder="—"
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-20 text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={msrp}
          disabled={busy}
          onChange={(e) => setMsrp(e.target.value)}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          className="w-12 text-xs border rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:border-gray-700"
          value={currency}
          disabled={busy}
          onChange={(e) => setCur(e.target.value)}
        />
      </td>
      <td className="py-1.5 pr-0 whitespace-nowrap">
        <button
          type="button"
          disabled={busy}
          className="text-xs text-indigo-600 hover:underline mr-2"
          onClick={() =>
            void onSave(row.id, {
              product_line,
              external_product_id: external_product_id.trim() || null,
              width_ft: Number(width_ft),
              length_ft: Number(length_ft),
              package_key: package_key.trim() || null,
              options_key: options_key.trim() || null,
              msrp: Number(msrp),
              currency: currency.trim() || "USD",
            })
          }
        >
          Save
        </button>
        <button
          type="button"
          disabled={busy}
          className="text-xs text-red-600 hover:underline"
          onClick={() => void onDelete(row.id)}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

export function OrgSettingsSectionSalesCatalogTable(props: {
  busy: boolean;
  items: SalesCatalogItemRow[];
  search: string;
  onSearchChange: (q: string) => void;
  onUpdateRow: (id: string, patch: Record<string, unknown>) => Promise<void>;
  onDeleteRow: (id: string) => Promise<void>;
}) {
  const { busy, items, search, onSearchChange, onUpdateRow, onDeleteRow } = props;
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return items;
    return items.filter((it) => {
      const hay = [it.product_line, it.external_product_id ?? "", String(it.width_ft), String(it.length_ft)]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, q]);

  if (!items.length && !search) return null;

  return (
    <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
      <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">Published snapshot rows</div>
      <p className="text-[11px] text-gray-500">
        Line id is internal. Set optional product id / SKU on import or here for search and integrations. Subscriber overrides still match manufacturer rows by
        product line, width, length, and optional package/options keys.
      </p>
      <input
        type="search"
        className="w-full max-w-md text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 dark:border-gray-700"
        placeholder="Filter by product line, product id, or size…"
        value={search}
        disabled={busy}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      {!items.length ? (
        <p className="text-xs text-gray-500">No rows in the active snapshot (publish a CSV first).</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <th className="py-1.5 pr-2">Line id</th>
                <th className="py-1.5 pr-2">Product id</th>
                <th className="py-1.5 pr-2">Product line</th>
                <th className="py-1.5 pr-2">W</th>
                <th className="py-1.5 pr-2">L</th>
                <th className="py-1.5 pr-2">Pkg</th>
                <th className="py-1.5 pr-2">Opt</th>
                <th className="py-1.5 pr-2">MSRP</th>
                <th className="py-1.5 pr-2">Cur</th>
                <th className="py-1.5 pr-0" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <RowEditor key={row.id} row={row} busy={busy} onSave={onUpdateRow} onDelete={onDeleteRow} />
              ))}
            </tbody>
          </table>
          {q && filtered.length === 0 ? <div className="p-2 text-xs text-gray-500">No rows match this filter.</div> : null}
        </div>
      )}
    </div>
  );
}
